import isIframe from './helpers/is-iframe'
import type { WidgetEventData, MessageData, ApiEventType, ApiErrorEvent, WorkflowExecutionPhase } from './types'
import useWidgetHooks from '@/composables/widget-hooks'
import { createOrchestratorApi } from '@/api/orchestrator'
import { createAppsApi } from '@/api/apps'
import useWidgetOptions from '@/composables/widget-options'
import useWidgetViewManager from '@/composables/widget-view-manager'
import { WidgetViews } from '@/types'
import { until, useEventBus } from '@vueuse/core'
import useTracker from '@/composables/tracker'

const handleParentMessage = async (message: MessageEvent<MessageData>) => {
    const { preInitialize, initialize, resetInitialization, reinitialize, initializeCustomPagePreview, initialized } = useWidgetHooks()

    const { type, data } = message.data

    // deprecated
    if (type === 'widget.setContext') {
        preInitialize(data.token, data?.options, data?.clientDomain)
    }

    if (type === 'widget.open' || type === 'widget.initialize') {
        emitEventToApi('widget-opened')
        initialize()
    }

    if (type === 'widget.close') {
        resetInitialization()
        if (data.emitEvent) {
            emitEventToApi('widget-closed')
        }
    }

    if (type === 'widget.reinitialize') {
        reinitialize()
    }

    if (type === 'widget.setOptions') {
        const { token, clientDomain, ...otherOptions } = data
        const { setOptions } = useWidgetOptions()
        if (token) {
            preInitialize(token, otherOptions, clientDomain)
        } else {
            setOptions(otherOptions)
        }
    }

    if (type === 'widget.showCustomPagePreview') {
        const { changeView } = useWidgetViewManager()
        initializeCustomPagePreview()

        changeView(WidgetViews.CUSTOM_PAGE_PREVIEW, {
            customPagePreview: true,
            customPagePreviewData: data,
        })
    }

    if (type === 'widget.updateCustomPagePreview') {
        useEventBus('update-custom-page-preview').emit(data)
    }

    if (type === 'widget.apiEventHandlerAdded') {
        if (!initialized.value) {
            await until(initialized).toBe(true)
        }
        useTracker('widget').track('apiEventAdded', '', { eventName: data.event })
    }
}

const postMessageToWrapper = (message: MessageData) => {
    if (!isIframe) return
    window.parent.postMessage(message, '*')
}

const initializeIframe = () => {
    window.addEventListener('message', handleParentMessage)

    setWorkflowExecutionPhase('init')

    createOrchestratorApi()

    createAppsApi()
    postMessageToWrapper({
        type: 'widget-wrapper.ready',
    })
}

const closeWidget = (emitEvent = true) => {
    const { options } = useWidgetOptions()
    postMessageToWrapper({
        type: 'widget-wrapper.close',
        data: {
            autoCloseWidget: options.value.autoCloseWidget,
            emitEvent,
        },
    })
}

const finishWorkflow = (data: WidgetEventData = null) => {
    emitEventToApi('workflow-finished', data)
}

const emitErrorEvent = (details: ApiErrorEvent) => {
    emitEventToApi('error', details)
}

const emitEventToApi = (event: ApiEventType, details?: unknown) => {
    postMessageToWrapper({
        type: 'widget-wrapper.api-event',
        data: {
            event,
            details,
        },
    })
}

const openDeeplink = (link: string) => {
    postMessageToWrapper({
        type: 'widget-wrapper.open-deeplink',
        data: {
            link,
        },
    })
}

const setIframeHeight = (data: string) => {
    postMessageToWrapper({
        type: 'widget-wrapper.set-height',
        data,
    })
}

const setWorkflowExecutionPhase = (data: WorkflowExecutionPhase) => {
    postMessageToWrapper({
        type: 'widget-wrapper.set-workflow-execution-phase',
        data,
    })
}

function useEmbed() {
    return {
        closeWidget,
        initializeIframe,
        finishWorkflow,
        emitEventToApi,
        openDeeplink,
        emitErrorEvent,
        setIframeHeight,
        setWorkflowExecutionPhase,
    }
}

export default useEmbed
